
import { addDays, addYears, parseISO, max, format } from 'date-fns'
import { VueDatePicker } from '@mathieustan/vue-datepicker'
import '@mathieustan/vue-datepicker/dist/vue-datepicker.min.css'
import analytics from '@/helpers/analytics'

export default {
  components: { VueDatePicker },
  props: {
    eventName: {
      type: String,
      default: ''
    },
    date: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    relatedDate: {
      type: String,
      default: null
    },
    borderless: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      visible: false,
      pickerDate: null,
      currentDate: new Date()
    }
  },
  computed: {
    nextDay () {
      return addDays(this.currentDate, 1)
    },
    nextYear () {
      return addYears(this.currentDate, 1)
    },
    nextDayFromRelatedDate () {
      return addDays(parseISO(this.relatedDate), 1)
    },
    minDate () {
      if (!this.relatedDate) return this.currentDate
      return max([this.nextDay, this.nextDayFromRelatedDate])
    },
    maxDate () {
      return this.nextYear
    },
    locale () {
      return { lang: this.$store.state.language }
    },
    dateFormat () {
      return this.locale === 'he' ? 'dd/MM/yyyy' : 'ddd, D MMM'
    }
  },
  watch: {
    relatedDate (relatedDate) {
      if (parseISO(this.pickerDate) < this.nextDayFromRelatedDate) {
        this.pickerDate = format(this.nextDayFromRelatedDate, 'yyyy-MM-dd')
      }
      this.emitChange()
      this.visible = true
      this._startCalendarObserver()
    }
  },
  created () {
    this.pickerDate = this.date
  },
  mounted () {
    this._closeFromOtherControls = () => this.closePicker()
    this.$root.$on('close-all-date-pickers', this._closeFromOtherControls)
  },
  beforeDestroy () {
    this._stopCalendarObserver()
    this.$root.$off('close-all-date-pickers', this._closeFromOtherControls)
  },
  methods: {
    onPickerOpen () {
      this.$root.$emit('close-all-airport-autocompletes')
      this._startCalendarObserver()
    },
    open () {
      this.$refs.picker.showDatePicker()
    },
    closePicker () {
      this._stopCalendarObserver()
      this.visible = false
      this.$nextTick(() => {
        const picker = this.$refs.picker
        if (picker && typeof picker.hideDatePicker === 'function') {
          picker.hideDatePicker()
        }
      })
    },
    emitChange () {
      this._stopCalendarObserver()
      this.visible = false
      if (this.eventName.length) {
        analytics.track(this.eventName, this, {
          unique: this.pickerDate,
          value: this.pickerDate
        })
      }
      this.$emit('update', this.pickerDate)
    },

    // ── Related-date highlight (shown when picking the end date) ──
    _getVisiblePopup () {
      return Array.from(document.querySelectorAll('.search-form-date-picker-content'))
        .find(el => el.offsetParent !== null) || null
    },
    _applyRelatedDateHighlight () {
      if (!this.relatedDate) return
      clearTimeout(this._highlightTimer)
      this._highlightTimer = setTimeout(() => {
        const popup = this._getVisiblePopup()
        if (!popup) return

        const targetEl = popup.querySelector(
          `.vd-picker__table-day[data-date="${this.relatedDate}"]`
        )

        // Remove stale markers; add to target only if missing
        popup.querySelectorAll('.vd-picker__table-day--range-from').forEach((el) => {
          if (el !== targetEl) el.classList.remove('vd-picker__table-day--range-from')
        })
        if (targetEl && !targetEl.classList.contains('vd-picker__table-day--range-from')) {
          targetEl.classList.add('vd-picker__table-day--range-from')
        }
      }, 10)
    },
    _startCalendarObserver () {
      if (!this.relatedDate) return
      this.$nextTick(() => {
        const popup = this._getVisiblePopup()
        if (!popup) return

        this._applyRelatedDateHighlight()

        if (this._calendarObserver) return
        this._calendarObserver = new MutationObserver(() => {
          this._applyRelatedDateHighlight()
        })
        this._calendarObserver.observe(popup, { childList: true, subtree: true })
      })
    },
    _stopCalendarObserver () {
      clearTimeout(this._highlightTimer)
      if (this._calendarObserver) {
        this._calendarObserver.disconnect()
        this._calendarObserver = null
      }
    }
  }
}
